import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

function fmt(t) {
  if (!isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function AudioPlayer({ src, title = "Audio preview" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onLoaded = () => setDur(a.duration || 0);
    const onTime = () => setCur(a.currentTime || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const seek = (e) => {
    const a = ref.current;
    if (!a || !dur) return;
    const bar = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - bar.left) / bar.width;
    a.currentTime = Math.max(0, Math.min(dur, pct * dur));
  };

  const pct = dur > 0 ? (cur / dur) * 100 : 0;

  return (
    <div className="brut-border bg-[#1F3D2A] text-white p-5" data-testid="audio-player">
      <div className="flex items-center justify-between mb-3">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">{title}</div>
        <div className="mono text-[10px] uppercase tracking-[0.25em] text-white/50">Preview only</div>
      </div>
      <audio ref={ref} src={src} preload="metadata" className="hidden" />
      <div className="flex items-center gap-4">
        <button
          data-testid="audio-play-btn"
          onClick={toggle}
          className="brut-border bg-[#C77E3C] text-white w-12 h-12 grid place-items-center hover:bg-white hover:text-[#1F3D2A] transition-colors flex-shrink-0"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div
            data-testid="audio-progress"
            onClick={seek}
            className="h-3 bg-white/10 border-2 border-white/20 cursor-pointer relative overflow-hidden"
          >
            <div className="absolute inset-y-0 left-0 bg-[#C77E3C] transition-[width] duration-100" style={{ width: `${pct}%` }} />
            {/* fake waveform bars for aesthetic */}
            <div className="absolute inset-0 flex items-center justify-around opacity-40 pointer-events-none">
              {Array.from({ length: 40 }).map((_, i) => (
                <div key={i} className="w-[2px] bg-white" style={{ height: `${20 + (i * 37) % 70}%` }} />
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between mt-2 mono text-[10px] tracking-[0.15em] text-white/70">
            <span>{fmt(cur)}</span>
            <span>{fmt(dur)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
